<div class="container-fluid p-0">
  <!-- Alert/Notice Panel - Hidden by default, can be toggled -->
  <div class="alert alert-info alert-dismissible fade show mb-4" role="alert">
    <strong>Campaign Management Notes:</strong>
    <ol class="mb-0 mt-2">
      <li>Active campaigns cannot be edited once they are live.</li>
      <li>Ensure all campaign details are accurate before activating.</li>
      <li>Campaign dates should be set within the project timeline.</li>
    </ol>
    <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
  </div>

  <!-- Action Bar -->
  <div class="d-flex justify-content-between align-items-center mb-4">
    <!-- New Button -->
    <button class="btn btn-primary new-button" (click)="createNewCampaign()">
      <i class="fa fa-plus-circle me-2"></i> New Campaign
    </button>

    <!-- Search Box -->
    <div class="search-box">
      <input 
        type="text" 
        class="form-control" 
        placeholder="Search campaigns" 
        [(ngModel)]="searchText"
        [disabled]="!campaigns || campaigns.length === 0">
      <button class="search-btn">
        <i class="fa fa-search"></i>
      </button>
    </div>
  </div>

  <!-- Main content container with card -->
  <div class="card shadow-sm">
    <div class="card-header bg-white py-3">
      <h5 class="mb-0">Campaign Details</h5>
    </div>
    
    <div class="card-body">
      <!-- Loading State -->
      <div class="text-center py-5" *ngIf="loading">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading campaigns...</p>
      </div>
      
      <!-- Empty State -->
      <div class="empty-state" *ngIf="!loading && (!campaigns || filteredCampaigns.length === 0)">
        <div class="text-center py-4">
          <i class="fa fa-calendar fa-3x text-muted mb-3"></i>
          <h5>No Campaigns Available</h5>
          <p class="text-muted">Click on "New Campaign" to create your first campaign</p>
        </div>
      </div>
      
      <!-- Campaigns Table -->
      <div class="table-responsive" *ngIf="!loading && filteredCampaigns && filteredCampaigns.length > 0">
        <table class="table table-hover campaign-table">
          <thead>
            <tr>
              <th>Campaign Name</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Offer Layout</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let campaign of filteredCampaigns">
              <td>{{ campaign.name }}</td>
              <td>{{ campaign.startDate | date:'mediumDate' }}</td>
              <td>{{ campaign.endDate | date:'mediumDate' }}</td>
              <td>{{ campaign.offerLayout }}</td>
              <td>
                <span class="badge" [ngClass]="getCampaignStatusClass(campaign)">
                  {{ campaign.status }}
                </span>
              </td>
              <td>
                <div class="campaign-actions">
                  <button class="btn action-btn" (click)="editCampaign(campaign)" 
                          [disabled]="!canEditCampaign(campaign)" title="Edit campaign">
                    <i class="fa fa-edit"></i>
                  </button>
                  
                  <button class="btn action-btn" (click)="duplicateCampaign(campaign)" 
                          title="Duplicate campaign">
                    <i class="fa fa-copy"></i>
                  </button>
                  
                  <button class="btn action-btn" (click)="toggleCampaignStatus(campaign)"
                          [title]="campaign.isActive ? 'Deactivate campaign' : 'Activate campaign'">
                    <i class="fa" [ngClass]="campaign.isActive ? 'fa-toggle-on text-success' : 'fa-toggle-off'"></i>
                  </button>
                  
                  <button class="btn action-btn" (click)="deleteCampaign(campaign)"
                          [disabled]="!canDeleteCampaign(campaign)" title="Delete campaign">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  
  <!-- New/Edit Campaign Form Container (hidden by default) -->
  <div class="campaign-form-container mt-4" *ngIf="showCampaignForm">
    <div class="card shadow-sm">
      <div class="card-header bg-white py-3 d-flex justify-content-between align-items-center">
        <h5 class="mb-0">{{ isEditMode ? 'Edit Campaign' : 'Create New Campaign' }}</h5>
        <button class="btn-close" (click)="cancelCampaignForm()"></button>
      </div>
      
      <div class="card-body">
        <form [formGroup]="campaignForm" (ngSubmit)="saveCampaign()">
          <div class="row g-3">
            <div class="col-md-6">
              <label for="campaignName" class="form-label">Campaign Name*</label>
              <input type="text" class="form-control" id="campaignName" formControlName="name">
              <div class="form-text text-danger" *ngIf="campaignForm.get('name')?.invalid && campaignForm.get('name')?.touched">
                Campaign name is required
              </div>
            </div>
            
            <div class="col-md-6">
              <label for="offerLayout" class="form-label">Offer Layout*</label>
              <select class="form-select" id="offerLayout" formControlName="offerLayout">
                <option value="">Select a layout</option>
                <option value="Grid">Grid</option>
                <option value="List">List</option>
                <option value="Carousel">Carousel</option>
              </select>
              <div class="form-text text-danger" *ngIf="campaignForm.get('offerLayout')?.invalid && campaignForm.get('offerLayout')?.touched">
                Offer layout is required
              </div>
            </div>
            
            <!-- Offer Layout Preview Section -->
            <div class="col-12 mt-2" *ngIf="campaignForm.get('offerLayout')?.value">
              <div class="card">
                <div class="card-header bg-light py-2">
                  <h6 class="mb-0">{{ campaignForm.get('offerLayout')?.value }} Layout Preview</h6>
                </div>
                <div class="card-body">
                  <div class="layout-preview" [ngSwitch]="campaignForm.get('offerLayout')?.value">
                    
                    <!-- Grid Layout -->
                    <div *ngSwitchCase="'Grid'" class="offer-grid-preview">
                      <div class="row">
                        <div class="col-md-4 mb-3">
                          <div class="offer-card">
                            <div class="offer-image">
                              <img src="assets/images/offer-grid-1.jpg" onerror="this.src='https://via.placeholder.com/300x200?text=Offer+Image'" alt="Special Offer" class="img-fluid">
                              <span class="discount-badge">20% OFF</span>
                            </div>
                            <div class="offer-content">
                              <h5>Summer Special</h5>
                              <p class="text-muted small">Valid till Aug 31, 2023</p>
                              <button class="btn btn-sm btn-outline-primary w-100">Get Offer</button>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-4 mb-3">
                          <div class="offer-card">
                            <div class="offer-image">
                              <img src="assets/images/offer-grid-2.jpg" onerror="this.src='https://via.placeholder.com/300x200?text=Offer+Image'" alt="Limited Deal" class="img-fluid">
                              <span class="discount-badge bg-danger">HOT DEAL</span>
                            </div>
                            <div class="offer-content">
                              <h5>Weekend Offer</h5>
                              <p class="text-muted small">Valid for 2 days only</p>
                              <button class="btn btn-sm btn-outline-primary w-100">Get Offer</button>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-4 mb-3">
                          <div class="offer-card">
                            <div class="offer-image">
                              <img src="assets/images/offer-grid-3.jpg" onerror="this.src='https://via.placeholder.com/300x200?text=Offer+Image'" alt="Premium Offer" class="img-fluid">
                              <span class="discount-badge bg-success">PREMIUM</span>
                            </div>
                            <div class="offer-content">
                              <h5>Exclusive Deal</h5>
                              <p class="text-muted small">For premium members</p>
                              <button class="btn btn-sm btn-outline-primary w-100">Get Offer</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- List Layout -->
                    <div *ngSwitchCase="'List'" class="offer-list-preview">
                      <div class="offer-list-item mb-3">
                        <div class="row align-items-center">
                          <div class="col-md-2">
                            <img src="assets/images/offer-list-1.jpg" onerror="this.src='https://via.placeholder.com/100x100?text=Offer'" alt="Offer" class="img-fluid rounded">
                          </div>
                          <div class="col-md-7">
                            <h5 class="mb-1">Special Summer Discount</h5>
                            <p class="text-muted mb-1 small">Get 20% off on all summer products</p>
                            <p class="text-success small mb-0">Valid till: Aug 31, 2023</p>
                          </div>
                          <div class="col-md-3 text-end">
                            <button class="btn btn-sm btn-primary">Claim Now</button>
                          </div>
                        </div>
                      </div>
                      <div class="offer-list-item mb-3">
                        <div class="row align-items-center">
                          <div class="col-md-2">
                            <img src="assets/images/offer-list-2.jpg" onerror="this.src='https://via.placeholder.com/100x100?text=Offer'" alt="Offer" class="img-fluid rounded">
                          </div>
                          <div class="col-md-7">
                            <h5 class="mb-1">Weekend Flash Sale</h5>
                            <p class="text-muted mb-1 small">Limited time offers on select items</p>
                            <p class="text-danger small mb-0">Ends in 2 days</p>
                          </div>
                          <div class="col-md-3 text-end">
                            <button class="btn btn-sm btn-primary">Claim Now</button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Carousel Layout -->
                    <div *ngSwitchCase="'Carousel'" class="offer-carousel-preview">
                      <div class="carousel-container">
                        <div class="active-offer">
                          <div class="offer-slide">
                            <img src="assets/images/offer-carousel-1.jpg" onerror="this.src='https://via.placeholder.com/800x400?text=Special+Offer+Slide'" alt="Special Offer" class="img-fluid rounded">
                            <div class="carousel-caption">
                              <h4>Limited Time Offer</h4>
                              <p>Exclusive deals for our valued customers</p>
                              <button class="btn btn-light">View Details</button>
                            </div>
                          </div>
                        </div>
                        <div class="carousel-indicators">
                          <span class="indicator active"></span>
                          <span class="indicator"></span>
                          <span class="indicator"></span>
                        </div>
                        <div class="carousel-navigation">
                          <button class="nav-btn prev">&lt;</button>
                          <button class="nav-btn next">&gt;</button>
                        </div>
                      </div>
                    </div>
                    
                  </div>
                  <div class="text-muted small mt-2">
                    <i class="fa fa-info-circle me-1"></i> This is a preview of how offers will appear to users with the selected layout.
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <label for="startDate" class="form-label">Start Date*</label>
              <input type="date" class="form-control" id="startDate" formControlName="startDate">
              <div class="form-text text-danger" *ngIf="campaignForm.get('startDate')?.invalid && campaignForm.get('startDate')?.touched">
                Valid start date is required
              </div>
            </div>
            
            <div class="col-md-6">
              <label for="endDate" class="form-label">End Date*</label>
              <input type="date" class="form-control" id="endDate" formControlName="endDate">
              <div class="form-text text-danger" *ngIf="campaignForm.get('endDate')?.invalid && campaignForm.get('endDate')?.touched">
                Valid end date is required
              </div>
              <div class="form-text text-danger" *ngIf="campaignForm.errors?.['dateRange']">
                End date must be after start date
              </div>
            </div>
            
            <div class="col-md-12">
              <label for="description" class="form-label">Description</label>
              <textarea class="form-control" id="description" rows="3" formControlName="description"></textarea>
            </div>
            
            <div class="col-12 mt-4">
              <hr>
              <div class="d-flex justify-content-end gap-2">
                <button type="button" class="btn btn-light" (click)="cancelCampaignForm()">Cancel</button>
                <button type="submit" class="btn btn-primary" [disabled]="!campaignForm.valid">
                  {{ isEditMode ? 'Update Campaign' : 'Create Campaign' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</div>

<style>
  /* Modern styling */
  :host {
    display: block;
    padding: 20px;
  }
  
  /* New button */
  .new-button {
    height: 40px;
    font-weight: 500;
    padding: 0 16px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  /* Search box */
  .search-box {
    position: relative;
    width: 300px;
  }
  
  .search-box input {
    height: 40px;
    padding-right: 40px;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    border: 1px solid #e0e0e0;
  }
  
  .search-btn {
    position: absolute;
    right: 0;
    top: 0;
    height: 40px;
    width: 40px;
    border: none;
    background: transparent;
    color: #6c757d;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* Card styling */
  .card {
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    overflow: hidden;
  }
  
  .card-header {
    border-bottom: 1px solid #f0f0f0;
  }
  
  /* Table styling */
  .campaign-table {
    margin-bottom: 0;
  }
  
  .campaign-table th {
    font-weight: 500;
    color: #495057;
    border-top: none;
    background-color: #f8f9fa;
  }
  
  .campaign-table td {
    vertical-align: middle;
    color: #212529;
  }
  
  /* Badge styling */
  .badge {
    font-weight: normal;
    padding: 0.35em 0.65em;
    border-radius: 4px;
  }
  
  .badge-active {
    background-color: #198754;
  }
  
  .badge-draft {
    background-color: #6c757d;
  }
  
  .badge-scheduled {
    background-color: #0d6efd;
  }
  
  .badge-expired {
    background-color: #dc3545;
  }
  
  /* Action buttons */
  .campaign-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .action-btn {
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background: transparent;
    color: #6c757d;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }
  
  .action-btn:hover {
    background: #f8f9fa;
    color: #0d6efd;
  }
  
  .action-btn[disabled] {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  /* Form container */
  .campaign-form-container {
    animation: slideDown 0.3s ease;
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Offer Preview Styles */
  /* Grid Layout */
  .offer-grid-preview {
    padding: 10px 0;
  }
  
  .offer-card {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .offer-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.15);
  }
  
  .offer-image {
    position: relative;
    height: 140px;
    overflow: hidden;
  }
  
  .offer-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .discount-badge {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: #0d6efd;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
  }
  
  .offer-content {
    padding: 15px;
    background-color: white;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }
  
  .offer-content h5 {
    margin-bottom: 6px;
    font-size: 1rem;
  }
  
  .offer-content p {
    margin-bottom: 12px;
  }
  
  .offer-content button {
    margin-top: auto;
  }
  
  /* List Layout */
  .offer-list-preview {
    padding: 10px 0;
  }
  
  .offer-list-item {
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    padding: 15px;
    transition: background-color 0.2s ease, transform 0.2s ease;
  }
  
  .offer-list-item:hover {
    background-color: #f8f9fa;
    transform: translateX(5px);
  }
  
  /* Carousel Layout */
  .offer-carousel-preview {
    padding: 10px 0;
  }
  
  .carousel-container {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  .active-offer {
    position: relative;
  }
  
  .offer-slide {
    position: relative;
  }
  
  .carousel-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
    color: white;
    padding: 20px;
    text-align: center;
  }
  
  .carousel-indicators {
    position: absolute;
    bottom: 10px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    gap: 8px;
  }
  
  .indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: rgba(255,255,255,0.5);
    cursor: pointer;
  }
  
  .indicator.active {
    background-color: white;
  }
  
  .carousel-navigation {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    transform: translateY(-50%);
    display: flex;
    justify-content: space-between;
    padding: 0 10px;
  }
  
  .nav-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: rgba(255,255,255,0.7);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    transition: background-color 0.2s ease;
  }
  
  .nav-btn:hover {
    background-color: white;
  }
</style>
